import React from 'react';

interface AuthLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
}

/**
 * Authentication Layout Component
 * 
 * Provides a clean, centered layout for authentication pages
 * like login, register, forgot password, etc.
 */
export function AuthLayout({ 
  children, 
  title = "SupportDesk", 
  subtitle 
}: AuthLayoutProps) {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Skip to Main Content Bypass Link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-white focus:text-primary-700 focus:font-medium focus:shadow-md focus:rounded-md focus:ring-2 focus:ring-primary-500 focus:outline-none"
      >
        Skip to main content
      </a>

      {/* Header Landmark */}
      <header className="flex-shrink-0" role="banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4 sm:py-5">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 2L2 7v10c0 5.55 3.84 9.74 9 11 5.16-1.26 9-5.45 9-11V7l-10-5z"/>
                </svg>
              </div>
              <span className="text-2xl font-bold text-gray-900 font-heading">
                SupportDesk
              </span>
            </div>
            <div className="hidden sm:block text-sm text-gray-500">
              Professional Support Desk Solution
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Landmark */}
      <main id="main-content" className="flex-1 flex items-center justify-center py-4 sm:py-6 px-4 sm:px-6 lg:px-8" role="main">
        <div className="w-full max-w-md space-y-4">
          {subtitle && (
            <div className="text-center">
              <p className="text-sm text-gray-600">{subtitle}</p>
            </div>
          )}
          {children}
        </div>
      </main>

      {/* Footer Landmark */}
      <footer className="flex-shrink-0" role="contentinfo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row items-center justify-between text-xs text-gray-500">
              <div>
                © {new Date().getFullYear()} SupportDesk MVP. Built with modern web technologies.
              </div>
              <div className="mt-2 sm:mt-0 text-gray-400">
                Professional Support Desk Solution
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

/**
 * Auth Card Wrapper
 * 
 * Provides a consistent card layout for authentication forms
 */
interface AuthCardProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
}

export function AuthCard({ children, title, subtitle }: AuthCardProps) {
  return (
    <div className="bg-white py-8 px-4 shadow-lg sm:rounded-lg sm:px-10 border border-gray-200">
      {(title || subtitle) && (
        <div className="text-center mb-8">
          {title && (
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {title}
            </h2>
          )}
          {subtitle && (
            <p className="text-sm text-gray-600">
              {subtitle}
            </p>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

/**
 * Auth Feature Highlights
 * 
 * Shows key features or benefits on auth pages
 */
export function AuthFeatures() {
  const features = [
    {
      icon: (
        <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: "Lightning Fast",
      description: "Quick response times and efficient ticket resolution"
    },
    {
      icon: (
        <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      title: "Secure & Reliable",
      description: "Enterprise-grade security with 99.9% uptime guarantee"
    },
    {
      icon: (
        <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
        </svg>
      ),
      title: "Team Collaboration",
      description: "Built for teams with powerful collaboration features"
    }
  ];

  return (
    <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-3">
      {features.map((feature, index) => (
        <div key={index} className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-md bg-primary-100">
            {feature.icon}
          </div>
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            {feature.title}
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            {feature.description}
          </p>
        </div>
      ))}
    </div>
  );
}

/**
 * Auth Background Pattern
 * 
 * Adds a subtle background pattern to auth pages
 */
export function AuthBackgroundPattern() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute left-1/2 top-0 -ml-3 h-16 w-6 bg-gradient-to-r from-transparent via-primary-200 to-transparent opacity-20 transform rotate-12"></div>
      <div className="absolute left-1/3 top-1/4 -ml-2 h-12 w-4 bg-gradient-to-r from-transparent via-primary-300 to-transparent opacity-15 transform -rotate-12"></div>
      <div className="absolute right-1/3 top-1/3 -ml-1 h-8 w-2 bg-gradient-to-r from-transparent via-primary-400 to-transparent opacity-10 transform rotate-45"></div>
      <div className="absolute right-1/4 bottom-1/3 -ml-2 h-10 w-4 bg-gradient-to-r from-transparent via-primary-300 to-transparent opacity-15 transform -rotate-45"></div>
      <div className="absolute left-1/4 bottom-1/4 -ml-3 h-14 w-6 bg-gradient-to-r from-transparent via-primary-200 to-transparent opacity-20 transform rotate-45"></div>
    </div>
  );
}